import { useState } from 'react';

import { hashData, filterObject } from '../01-core/Utilities';
import { Button } from '../02-atoms/Button';
import { Media } from '../02-atoms/Media';
import { Avatar, AvatarProps } from '../03-molecules/Avatar';

export interface AvatarListProps {
  /** Avatar objects */
  items: AvatarProps[];
  /** Show all label */
  showall: string;
  /** Close label */
  close: string;
  /** Arbitrary data-* attributes forwarded to the root element */
  [dataAttribute: `data-${string}`]: string | number | undefined;
}

/** Block rendering multiple avatars */
export const AvatarList = ({
  items,
  showall,
  close,
  ...props
}: AvatarListProps) => {
  const id = 'avatar-list-' + hashData(items);
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  return (
    <div
      className={['avatar-list', expanded ? 'avatar-list--expanded' : ''].join(
        ' ',
      )}
      {...props}
    >
      <button
        className="avatar-list__toggle"
        onClick={toggleExpanded}
        title={expanded ? close : showall}
        aria-haspopup="true"
        aria-controls={id}
        aria-expanded={expanded ? 'true' : 'false'}
        aria-hidden={expanded ? 'true' : 'false'}
      >
        {items.map((item, key) => {
          return key > 4
            ? ''
            : item.image && (
                <Media
                  key={key}
                  aspect="square"
                  alt={item.name}
                  {...filterObject(item.image, [], ['aspect'])}
                />
              );
        })}
        {items.length > 5 && (
          <span className="avatar-list__toggle-count">+{items.length - 5}</span>
        )}
      </button>
      <ul className="avatar-list__contents" id={id}>
        {items.map((item, key) => {
          return (
            <li className="avatar-list__item" key={key}>
              <Avatar {...item} />
            </li>
          );
        })}
      </ul>
      <Button
        className="avatar-list__close"
        onClick={toggleExpanded}
        type="tertiary"
        label={close}
        icon="chevron_up"
        icon_after={true}
        aria-haspopup="true"
        aria-controls={id}
        aria-expanded={expanded ? 'true' : 'false'}
        aria-hidden={!expanded ? 'true' : 'false'}
        tabIndex={expanded ? 0 : -1}
      />
    </div>
  );
};
